import { useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import type { Brand } from "../../../shared/types";
import {
  useResearchRuns,
  useCreateResearchPackage,
  type ResearchRun,
} from "../hooks/useMarketResearch";
import { buildCorpusPath } from "../lib/brand-navigation";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { SeoResearchControl } from "../components/research/SeoResearchControl";

type BrandOutletContext = {
  brand: Brand;
  brandId: string;
};

function statusBadge(status: string) {
  const color =
    status === "completed" ? "bg-green-100 text-green-800"
    : status === "approved" ? "bg-green-100 text-green-800"
    : status === "pending" ? "bg-yellow-100 text-yellow-800"
    : "bg-slate-100 text-slate-600";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
      {status}
    </span>
  );
}

function RunRow({
  brandId,
  run,
  onCreatePackage,
  creatingPackageForRunId,
}: {
  brandId: string;
  run: ResearchRun;
  onCreatePackage: (runId: string) => Promise<void>;
  creatingPackageForRunId: string | null;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition-colors hover:border-brand-200 hover:shadow-md">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Link
          to={`/brand/${brandId}/research/${run.id}`}
          className="min-w-0 flex-1 text-left hover:text-brand-700"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-medium text-slate-900">
              {run.topic ?? run.run_date}
            </span>
            <div className="flex items-center gap-2">
              {statusBadge(run.status)}
              <span className="text-slate-400">→</span>
            </div>
          </div>
          <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-500">
            <span>{run.run_date}</span>
            <span>{run.keywords_researched_count} keywords</span>
            <span>{run.opportunities_count} opp · {run.content_ideas_count} ideas · {run.threats_count} threats</span>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              void onCreatePackage(run.id);
            }}
            disabled={creatingPackageForRunId === run.id}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-400 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {creatingPackageForRunId === run.id ? "Opening…" : "Research Package"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function BrandResearch() {
  const { brandId: routeBrandId } = useParams<{ brandId: string }>();
  const { brand, brandId: outletBrandId } = useOutletContext<BrandOutletContext>();
  const brandId = routeBrandId ?? outletBrandId;
  const navigate = useNavigate();
  const [packageError, setPackageError] = useState<string | null>(null);

  const { data, isLoading, isError } = useResearchRuns(brandId);
  const createResearchPackage = useCreateResearchPackage(brandId);
  const runs = data?.data ?? [];
  const [creatingPackageForRunId, setCreatingPackageForRunId] = useState<string | null>(null);

  async function handleCreatePackage(runId: string) {
    if (!brandId) return;
    if (creatingPackageForRunId === runId) return;
    const currentRunId = runId;
    setPackageError(null);
    setCreatingPackageForRunId(currentRunId);
    try {
      const result = await createResearchPackage.mutateAsync({ runId: currentRunId });
      navigate(`/brand/${brandId}/research/packages/${result.data.id}`);
    } catch (error) {
      setPackageError(error instanceof Error ? error.message : "Could not open a research package for this run.");
    } finally {
      setCreatingPackageForRunId((value) => (value === currentRunId ? null : value));
    }
  }

  return (
    <div className="space-y-5">
      <WorkspaceHeader
        eyebrow="Research Archive"
        title="Research Documents"
        description={`Review archived research Runs and packages for ${brand.name}. Launch new research from Actions or Agent UI, then return here to inspect durable Results and open package work.`}
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: "Actions", href: "/tasks" },
          { label: "Research Documents" },
        ]}
        badges={(
          <span className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
            {runs.length} run{runs.length === 1 ? "" : "s"}
          </span>
        )}
        metadata={(
          <>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              Research / archive building
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">Packages and corpus handoff</span>
          </>
        )}
        actions={(
          <>
            <Link
              to="/tasks"
              className="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Open Actions
            </Link>
            <Link
              to={buildCorpusPath(brand.id)}
              className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Open corpus
            </Link>
          </>
        )}
      />

      <SeoResearchControl key={brandId} brandId={brandId} />

      <div className="rounded-[20px] border border-brand-100 bg-brand-50 p-4 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">Action to Result</p>
        <p className="mt-2 text-sm text-slate-700">
          Start new market research from <Link to="/tasks" className="font-medium text-brand-700 hover:text-brand-800 hover:underline">Actions</Link> or the agent. Use this page to review saved Runs and convert strong Results into packages.
        </p>
        <p className="mt-2 text-sm text-slate-700">
          Start one-off research from Actions. Keep recurring schedule changes on the market research Action instead of this archive view.
        </p>
      </div>

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Past Runs</h2>
            <p className="mt-1 text-xs text-slate-400">
              Open a run to review the research document, then convert strong runs into packages or save the report into Corpus.
            </p>
          </div>
          <span className="text-xs text-slate-400">{runs.length} shown</span>
        </div>

        {isLoading && (
          <div className="flex min-h-[120px] items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          </div>
        )}

        {isError && <p className="text-sm text-red-600">Could not load research runs.</p>}
        {packageError && (
          <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {packageError}
          </p>
        )}

        {!isLoading && !isError && !runs.length && (
          <p className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
            No research documents yet. Run the market research Action from Actions to begin building an archive for Corpus.
          </p>
        )}

        <div className="space-y-3">
          {runs.map((run) => (
            <RunRow
              key={run.id}
              brandId={brandId}
              run={run}
              onCreatePackage={(runId) => handleCreatePackage(runId)}
              creatingPackageForRunId={creatingPackageForRunId}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
